import type { App } from 'vue'
import * as elIcons from '@element-plus/icons-vue'
import router from '/@/router/index'
import { useNavTabs } from '/@/stores/navTabs'
import { usePermission } from '/@/stores/permission'
import { ElForm } from 'element-plus'
import { useSiteConfig } from '../stores/siteConfig'
import { useTitle } from '@vueuse/core'
import { i18n } from '../lang'
import { getUrl } from './axios'
import { kebabCase, pickBy, trimStart } from 'lodash-es'
import { isObject } from '/@/utils/is'

export function registerIcons (app: App) {
  /*
   * 全局注册element Plus的icon
   */
  const icons = elIcons as any
  for (const i in icons) {
    app.component(`el-icon-${kebabCase(icons[i].name)}`, icons[i])
  }
}

/**
 * 加载网络css文件
 * @param url css资源url
 */
export function loadCss (url: string): void {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = url
  link.crossOrigin = 'anonymous'
  document.getElementsByTagName('head')[0].appendChild(link)
}

/**
 * 加载网络js文件
 * @param url js资源url
 */
export function loadJs (url: string): void {
  const link = document.createElement('script')
  link.src = url
  document.body.appendChild(link)
}

/**
 * 根据路由 meta.title 设置浏览器标题
 */
export function setTitleFromRoute () {
  if (typeof router.currentRoute.value.meta.title != 'string') {
    return
  }
  nextTick(() => {
    let webTitle = ''
    if ((router.currentRoute.value.meta.title as string).indexOf('pagesTitle.') === -1) {
      webTitle = router.currentRoute.value.meta.title as string
    } else {
      webTitle = i18n.global.t(router.currentRoute.value.meta.title as string)
    }
    const title = useTitle()
    const siteConfig = useSiteConfig()
    title.value = `${webTitle}${siteConfig.siteName ? ' - ' + siteConfig.siteName : ''}`
  })
}

/**
 * 设置浏览器标题-只能在路由加载完成后调用
 * @param webTitle 新的标题
 */
export function setTitle (webTitle: string) {
  const title = useTitle()
  const siteConfig = useSiteConfig()
  title.value = `${webTitle}${siteConfig.siteName ? ' - ' + siteConfig.siteName : ''}`
}

/**
 * 是否是外部链接
 * @param path
 */
export function isExternal (path: string): boolean {
  return /^(https?|ftp|mailto|tel):/.test(path)
}

/**
 * 防抖
 * @param fn 执行函数
 * @param ms 间隔毫秒数
 */
export const debounce = (fn: Function, ms: number) => {
  return (...args: any[]) => {
    if (window.lazy) {
      clearTimeout(window.lazy)
    }
    window.lazy = setTimeout(() => {
      fn(...args)
    }, ms)
  }
}

/**
 * 根据pk字段的值从数组中获取key
 * @param arr
 * @param pk
 * @param value
 */
export const getArrayKey = (arr: any, pk: string, value: string): any => {
  for (const key in arr) {
    if (arr[key][pk] == value) {
      return key
    }
  }
  return false
}

/**
 * 表单重置
 * @param formEl
 */
export const onResetForm = (formEl: InstanceType<typeof ElForm> | undefined) => {
  if (!formEl) return
  formEl.resetFields && formEl.resetFields()
}

/**
 * 将数据构建为ElTree的data {label:'', children: []}
 * @param data
 */
export const buildJsonToElTreeData = (data: any): ElTreeData[] => {
  if (typeof data == 'object') {
    const childrens = []
    for (const key in data) {
      childrens.push({
        label: key + ': ' + data[key],
        children: buildJsonToElTreeData(data[key])
      })
    }
    return childrens
  } else {
    return []
  }
}

/**
 * 是否在后台应用内
 * @param path 不传递则通过当前路由 path 检查
 */
export const isAdminApp = (path = '') => {
  if (path) {
    return /^\/admin/.test(path)
  }
  if (/^\/admin/.test(router.currentRoute.value.fullPath) || window.location.hash.indexOf('#/admin') === 0) {
    return true
  }
  return false
}

/**
 * 是否为手机设备
 */
export const isMobile = () => {
  return !!navigator.userAgent.match(
    /android|webos|ip(hone|ad|od)|opera (mini|mobi|tablet)|iemobile|windows.+(phone|touch)|mobile|fennec|kindle (Fire)|Silk|maemo|blackberry|playbook|bb10\; (touch|kbd)|Symbian(OS)|Ubuntu Touch/i
  )
}

/**
 * 获取路由 path
 */
export const getCurrentRoutePath = () => {
  let path = router.currentRoute.value.path
  if (path == '/') path = trimStart(window.location.hash, '#')
  if (path.indexOf('?') !== -1) path = path.replace(/\?.*/, '')
  return path
}

/**
 * 从一个文件路径中获取文件名
 * @param path 文件路径
 */
export const getFileNameFromPath = (path: string) => {
  const paths = path.split('/')
  return paths[paths.length - 1]
}

/**
 * 鉴权
 * 提供 string 将根据当前路由 path 自动拼接和鉴权，还可以提供路由的 perms 属性进行鉴权
 * 方式一: v-auth="'export'"
 * 方式二: v-auth="{ perms: 'button:hp:export' }"
 * @param node
 */
export const auth = (node: string | { perms?: string }) => {
  const navTabs = useNavTabs()
  const permissionStore = usePermission()
  const permissions = permissionStore.getPermissions()
  if (typeof node === 'string') {
    const path = getCurrentRoutePath()
    if (navTabs.state.authNode.has(path)) {
      const subNodeName = path + (path == '/' ? '' : '/') + node
      if (navTabs.state.authNode.get(path)!.some((v: string) => v == subNodeName)) {
        return true
      }
    }
  } else {
    if (!node.perms) return false

    if (permissions?.includes(node.perms)) return true
  }
  return false
}

/**
 * 获取资源完整地址
 * @param relativeUrl 资源相对地址
 * @param domain 指定域名
 */
export const fullUrl = (relativeUrl: string, domain = '') => {
  const siteConfig = useSiteConfig()
  if (!domain) {
    domain = siteConfig.cdnUrl ? siteConfig.cdnUrl : getUrl()
  }
  if (!relativeUrl) return domain

  const regUrl = new RegExp(/^http(s)?:\/\//)
  const regexImg = new RegExp(/^((?:[a-z]+:)?\/\/|data:image\/)(.*)/i)
  if (!domain || regUrl.test(relativeUrl) || regexImg.test(relativeUrl)) {
    return relativeUrl
  }
  return domain + relativeUrl
}

/**
 * 文件类型效验，主要用于云存储
 * 服务端并不能单纯此函数来限制文件上传
 * @param {string} fileName 文件名
 * @param {string} fileType 文件mimetype，不一定存在
 */
export const checkFileMimetype = (fileName: string, fileType: string) => {
  if (!fileName) return false
  const siteConfig = useSiteConfig()
  const mimetype = siteConfig.upload.mimetype.toLowerCase().split(',')

  const fileSuffix = fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase()
  if (siteConfig.upload.mimetype === '*' || mimetype.includes(fileSuffix) || mimetype.includes('.' + fileSuffix)) {
    return true
  }
  if (fileType) {
    const fileTypeTemp = fileType.toLowerCase().split('/')
    if (mimetype.includes(fileTypeTemp[0] + '/*') || mimetype.includes(fileType)) {
      return true
    }
  }
  return false
}

/**
 * 获取一组资源的完整地址
 * @param relativeUrls 资源相对地址
 * @param domain 指定域名
 */
export const arrayFullUrl = (relativeUrls: string | string[], domain = '') => {
  if (typeof relativeUrls === 'string') {
    relativeUrls = relativeUrls == '' ? [] : relativeUrls.split(',')
  }
  for (const key in relativeUrls) {
    relativeUrls[key] = fullUrl(relativeUrls[key], domain)
  }
  return relativeUrls
}

/**
 * 根据当前时间生成问候语
 */
export const getGreet = () => {
  const now = new Date()
  const hour = now.getHours()
  let greet = ''

  if (hour < 5) {
    greet = i18n.global.t('utils.Late at night, pay attention to your body!')
  } else if (hour < 9) {
    greet = i18n.global.t('utils.good morning!') + i18n.global.t('utils.welcome back')
  } else if (hour < 12) {
    greet = i18n.global.t('utils.Good morning!') + i18n.global.t('utils.welcome back')
  } else if (hour < 14) {
    greet = i18n.global.t('utils.Good noon!') + i18n.global.t('utils.welcome back')
  } else if (hour < 18) {
    greet = i18n.global.t('utils.good afternoon') + i18n.global.t('utils.welcome back')
  } else if (hour < 24) {
    greet = i18n.global.t('utils.Good evening') + i18n.global.t('utils.welcome back')
  } else {
    greet = i18n.global.t('utils.Hello!') + i18n.global.t('utils.welcome back')
  }
  return greet
}

/**
 * 对象数组去重
 * @param {*} arr 数组
 * @param {*} key 去重key
 * @returns
 */
export const objectArrayDoWeight = function (arr: Array<anyObj> = [], key = 'name') {
  const temp: any[] = []
  arr.forEach(function (a) {
    const check = temp.every(function (b) {
      return a[key] !== b[key]
    })
    check ? temp.push(a) : ''
  })
  return temp
}

export type TreeNode<T> = {
  [P in keyof T]: T[P]
} & {
  children?: Array<TreeNode<T>>
}
/**
 * 树级数据深度优先遍历
 * @param treeData
 * @param callback (data, parent) => {} 回调函数一个参数是当前遍历项，第二个参数是当前项父级（根节点的parent===null）
 * @param childrenKey children的key
 */
export const treeDepthFirstTraversal = (
  treeData: TreeNode<anyObj>,
  callback: (treeData: TreeNode<anyObj>, parent: TreeNode<anyObj> | null) => void = () => {},
  childrenKey = 'children'
): void => {
  if (Object.keys(treeData).length > 0) {
    callback(treeData, null)
  }
  if (treeData[childrenKey] && treeData[childrenKey].length > 0) {
    recursion(treeData[childrenKey], treeData)
  }
  function recursion (list: Array<TreeNode<anyObj>>, parent: TreeNode<anyObj> | null) {
    list.forEach(listItem => {
      callback(listItem, parent)
      if (listItem[childrenKey] && listItem[childrenKey].length > 0) {
        recursion(listItem[childrenKey], listItem)
      }
    })
  }
}

/**
 * list数据深度优先遍历
 * @param list
 * @param callback (listItem, parent) => {} 回调函数一个参数是当前遍历项，第二个参数是当前项父级（根节点的parent===null）
 * @param childrenKey children的key
 */
export const listDepthFirstTraversal = (
  list: Array<anyObj> = [],
  callback: (listItem: anyObj, parent: any) => void = () => {},
  childrenKey = 'children'
): void => {
  recursion(list, null)
  function recursion (list: Array<anyObj> = [], parent: any) {
    list.forEach((listItem: anyObj) => {
      callback(listItem, parent)
      if (listItem[childrenKey] && listItem[childrenKey].length > 0) {
        recursion(listItem[childrenKey], listItem)
      }
    })
  }
}

/**
 * list数据深度优先遍历-添加parent
 * @param list
 * @param callback (listItem, parent) => {} 回调函数一个参数是当前遍历项，第二个参数是当前项父级（根节点的parent===null）
 * @param childrenKey children的key
 */
export const listDepthFirstTraversalAddParent = (
  list: Array<anyObj> = [],
  callback: (listItem: anyObj, parent: any) => void = () => {},
  childrenKey = 'children'
): void => {
  recursion(list, null)
  function recursion (list: Array<anyObj> = [], parent: any) {
    list.forEach((listItem: anyObj) => {
      listItem.parent = parent
      callback(listItem, parent)
      if (listItem[childrenKey] && listItem[childrenKey].length > 0) {
        recursion(listItem[childrenKey], listItem)
      }
    })
  }
}

/**
 * treeNode往上遍历拼接完整根节点到当前节点values
 * @param node
 * @returns
 */
export const treeNodeConcatCompleteValues = (node: anyObj): string[] => {
  if (!node) {
    return []
  }

  const values: string[] = [ node.value ]

  const recursion = (node: anyObj) => {
    if (node.parent) {
      values.unshift(node.parent.value as string)
      recursion(node.parent)
    }
  }

  recursion(node)

  return values
}

/**
 * 给树级数据增加mesh层级（mesh从0开始）
 * @param treeData
 * @param childrenKey children的key
 */
export const treeAddMesh = (treeData: TreeNode<anyObj> = {}, childrenKey = 'children'): void => {
  treeDepthFirstTraversal(treeData, (itemData, parent) => {
    if (!parent) {
      itemData.mesh = 0
    } else {
      itemData.mesh = parent.mesh + 1
    }
  }, childrenKey)
}

/**
 * 给树级数据增加mesh层级（mesh从0开始）
 * @param treeData
 * @param childrenKey children的key
 */
export const listAddMesh = (list: Array<anyObj> = [], childrenKey = 'children'): void => {
  listDepthFirstTraversalAddParent(list, (itemData, parent) => {
    if (!parent) {
      itemData.mesh = 0
    } else {
      itemData.mesh = parent.mesh + 1
    }
  }, childrenKey)
}

/**
 * 平级List转list树
 * @param list
 * @param parentId // 固定传入null
 * @returns
 */
export const flatListToListTree = ({
  list,
  parentId,
  idKey = 'id',
  parentIdKey = 'parentId'
}: {
  list: Array<anyObj>
  parentId?: string | null,
  idKey?: string
  parentIdKey?: string
}): Array<anyObj> => {
  const tree = []
  for (const item of list) {
    if (item[parentIdKey] === parentId) {
      const children = flatListToListTree({ list, parentId: item[idKey], idKey, parentIdKey })
      if (children.length > 0) {
        item.children = children
      }
      tree.push(item)
    }
  }
  return tree
}

/**
 * 移除数组对象无用 KEY
 * @param arr
 * @param keys
 */
export const purifyingObject = <T, K extends keyof T>(arr: T[], keys: K[]): T[] => {
  // 遍历数组中的每个对象
  return arr.map(obj => {
    // 创建新对象，复制旧对象上所有属性
    const newObj = { ...obj }
    // 删除指定的 key
    keys.forEach(key => delete newObj[key])
    // 返回新对象
    return newObj
  })
}

/**
 * 扁平化对象数组
 * @param {*} arr 多维对象数组
 * @param {*} children 孩子节点字段 默认 'children'
 */
export const normalize = (arr: any[], children = 'children'): any[] => {
  const config = {
    childrenList: children
  }

  const flattenedArray: any[] = []

  const flattenRecursive = (item: any) => {
    flattenedArray.push(item)
    const childrenList = item[config.childrenList] || []
    childrenList.forEach((child: any) => flattenRecursive(child))
  }

  arr.forEach((item: any) => flattenRecursive(item))

  return flattenedArray
}

/**
 * 移除对象中的指定键
 * @param obj - 要过滤的对象
 * @param keys - 要移除的键数组
 * @returns 过滤后的对象
 */
export const removeKeysFromObject = (obj: anyObj | null, keys: string[]): anyObj | null => {
  if (!obj) return null
  const newObj = { ...obj }
  keys.forEach(key => delete newObj[key])
  return newObj
}

/**
 * 下载文件流
 * @param res 文件流(string)或标准返回值(headers: anyObj, data: any)
 * @param filename 文件名 当不传且res为标准返回值(headers: anyObj, data: any)时取响应头
 * @param config 配置项 { useFileNameTimestamp: 是否使用文件名后缀增加timestamp, 默认为true }
 */
export const downloadFileStream = (
  res: any,
  filename?: string,
  config: { useFileNameTimestamp?: boolean } = { useFileNameTimestamp: true }
) => {
  const blob = new Blob([ isObject(res) ? res.data : res ], { type: 'application/vnd.ms-excel' })

  let formatFileName = filename
  if (!formatFileName && isObject(res)) {
    const contentDisposition = (res?.headers && res?.headers['content-disposition']) || ''
    const contentDispositionObj = contentDisposition
      .split(';')
      .filter((item: string) => item.includes('='))
      .map((item: string) => item.split('='))
      .map((item: string[]) => ({ [item[0]]: item[1] }))
      .reduce((pre: anyObj, cur: anyObj) => ({ ...pre, ...cur }))
    contentDispositionObj.filename && (formatFileName = decodeURIComponent(contentDispositionObj.filename))
  }

  if (config?.useFileNameTimestamp) {
    const timestamp = new Date().getTime()
    try {
      const [ filenamePrefix = '', filenameSuffix = '' ] = (formatFileName || '').split('.')
      formatFileName = `${filenamePrefix}_${timestamp}.${filenameSuffix}`
    } catch (error) {}
  }

  const nav = window.navigator as any
  if (nav.msSaveOrOpenBlob) {
    nav.msSaveOrOpenBlob(blob, formatFileName)
  } else {
    const href = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.style.display = 'none'
    a.href = href
    a.download = formatFileName || ''
    a.click()
    URL.revokeObjectURL(a.href)
  }
}

/**
 * 动态改变网址参数
 * @param routeParams
 */
export const dynamicChangeRouteParams = function (routeParams: anyObj = {}) {
  let newUrl = '' + router.currentRoute.value.path // publicPath需要手动加上
  if (Object.keys(routeParams).length > 0) {
    let routeParamsString = '?'
    Object.keys(routeParams).forEach(key => {
      routeParamsString += `${key}=${routeParams[key]}&`
    })
    routeParamsString = routeParamsString.slice(0, -1)
    newUrl += routeParamsString
    // window.history.replaceState('', '', newUrl)
  }
  window.history.replaceState('', '', newUrl)
}

/**
 * 改变当前路由参数
 * @param routeParams
 */
export const setCurrentRouteQuery = function (routeParams: anyObj = {}) {
  const pureRouteParams = pickBy(routeParams)
  // 更新网址
  dynamicChangeRouteParams(pureRouteParams)
  // 更新query
  Object.keys(pureRouteParams).forEach(key => {
    router.currentRoute.value.query[key] = routeParams[key]
  })
  // 更新fullPath
  const fullPathParams = Object.keys(pureRouteParams)
    .map(key => `${key}=${pureRouteParams[key]}`)
    .join('&')
  router.currentRoute.value.fullPath = Object.keys(pureRouteParams).length
    ? router.currentRoute.value.path + '?' + fullPathParams
    : router.currentRoute.value.path
  useNavTabs().updateTab(router.currentRoute.value)
}

/**
 * 获取当前路由参数
 * @returns
 */
export const getCurrentRouteQuery = (): anyObj => {
  return router.currentRoute.value.query
}

/**
 * 计算加总值
 * @param list
 * @param field
 */
export const calcTotalCount = (list = [], field: string) => {
  return list.reduce((accum: number, item: any) => accum + item[field], 0)
}

/**
 * 获取数组最深层级
 * @param arr
 * @param attr 子级属性
 * @param index
 * @returns {number}
 */
export function getArrayDeep (arr: any, attr = 'children', index = 1) {
  let newIndex = index
  for (const iterator of arr) {
    let tempIndex = index
    if (iterator[attr] && iterator[attr].length) {
      tempIndex = getArrayDeep(iterator[attr], attr, index + 1)
      newIndex = Math.max(tempIndex, newIndex)
    }
  }
  return newIndex
}

export const escapeRegexpString = (value = '') => String(value).replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')


/**
 * 首字母排序
 * @param list
 * @param sort
 * @param key 需要排序的key，如果是string[]不用传
 * @returns
 */
export const sortByFirstChart = (list: Array<any>, sort: 'up' | 'down' = 'up', key?: string) => {
  return list.sort((a, b) => {
    if (sort === 'up') {
      if (key) {
        return String(a[key].split('')[0])
          .toLowerCase()
          .localeCompare(String(b[key].split('')[0]).toLowerCase())
      } else {
        return String(a.split('')[0])
          .toLowerCase()
          .localeCompare(String(b.split('')[0]).toLowerCase())
      }
    } else {
      if (key) {
        return String(b[key].split('')[0])
          .toLowerCase()
          .localeCompare(String(a[key].split('')[0]).toLowerCase())
      } else {
        return String(b.split('')[0])
          .toLowerCase()
          .localeCompare(String(a.split('')[0]).toLowerCase())
      }
    }
  })
}

/**
 * 根据值获取枚举对象中的键
 * @param value 需要查找的值
 * @param enumObj 枚举对象
 * @returns 找到的值对应的键，如果没有找到则返回 undefined
 */
export const getKeyByValue = (value: number, enumObj: any): string | undefined => {
  return Object.entries(enumObj).find(([ key, val ]) => val === value)?.[0]
}

/**
 * 从一个嵌套的对象数组中提取特定属性的值，并将这些值扁平化为一个字符串数组
 * @param data
 * @param config
 */
export const flattenProps = ({
  data,
  config
} : {
  data: anyObj[];
  config?: { prop?: string, children?: string }
}): string[] => {
  const props: string[] = []

  const prop = config?.prop || 'value'
  const children = config?.children || 'children'

  function extractProps (items: anyObj[]) {
    for (const item of items) {
      if (item[prop]) {
        props.push(item[prop])
      }
      if (item[children]) {
        extractProps(item[children])
      }
    }
  }

  extractProps(data)
  return props
}
